import Alpine from 'alpinejs'
import MorphPlugin from '@alpinejs/morph'

import {
  generateUrl,
  getQueryParamsObject,
  getRequestUriFromUrl,
  parseFormData,
  readPageFromPath,
  stripPageFromPath,
} from './interceptor-helpers.js'

Alpine.plugin(MorphPlugin)

/**
 * @type {import('alpinejs').DirectiveCallback}
 */
export const runInterceptor = (el, { expression }, { evaluate, cleanup }) => {
  const { endpoint, morph = true, viewTransition = true, nonce } = evaluate(expression)

  /**
   * Track the handled events, to avoid duplicate interception requests
   * @type {WeakSet<Event>}
   */
  const handledEvents = new WeakSet()

  /**
   * @param {PopStateEvent} event
   */
  function popstateHandler(event) {
    if (event.state?.xIntercept === true) {
      applyUpdatedContent({ page: event.state.page, queryParams: event.state.queryParams })
    }
  }

  /**
   * @returns {Promise<string>}
   */
  function getHtml({ endpoint, queryParams = {} } = {}) {
    const currentUrl = new URL(window.location.href)
    const apiBase =
      document.head.querySelector('link[rel="https://api.w.org/"]')?.href ?? '/wp-json/'

    const fetchUrl = new URL(endpoint, apiBase)
    fetchUrl.searchParams.set('current', getRequestUriFromUrl(currentUrl))
    for (const [key, value] of Object.entries(queryParams)) {
      fetchUrl.searchParams.set(key, value)
    }

    return fetch(fetchUrl, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-WP-Nonce': nonce,
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch ${fetchUrl}`)
        }
        return response
      })
      .then(response => response.json())
      .then(result => result.output)
  }

  /**
   * Given a page number and query parameters, replace the content
   * of the replaceable element with the REST-rendered HTML
   */
  async function applyUpdatedContent({ page, queryParams }) {
    const focusedElement = el.contains(document.activeElement) ? document.activeElement : undefined

    // Make element inert to avoid interactions while updating
    el.inert = true

    const markAsLoading = () => {
      el.dataset.interceptLoading = true
    }
    if (viewTransition && document.startViewTransition) {
      document.startViewTransition(markAsLoading)
    } else {
      markAsLoading()
    }

    const concreteEndpoint =
      typeof endpoint === 'function'
        ? endpoint({
            morph,
            viewTransition,
            page,
            queryParams,
          })
        : endpoint

    await getHtml({
      endpoint: concreteEndpoint,
      queryParams: {
        paged: page,
        query_params: JSON.stringify(queryParams),
      },
    })
      .then(content => {
        // Use document fragment instead of just setting outerHTML to be able to
        // properly set focus after replacing
        const template = document.createElement('template')
        template.innerHTML = content
        const newContentFragment = template.content
        const newElement = newContentFragment.firstElementChild

        // There may be stylesheets inside the content that need kept
        // (but the REST request may not include them)
        const stylesheets = el.querySelectorAll('link[id][rel=stylesheet]')
        const missingStylesheets = Array.from(stylesheets).filter(
          stylesheet =>
            !document.head.querySelector(`link[rel=stylesheet][id="${stylesheet.id}"]`) &&
            !newElement.querySelector(`link[rel=stylesheet][id="${stylesheet.id}"]`),
        )

        // Move missing stylesheets to the document head
        document.head.append(...missingStylesheets)

        // Remove inert state & re-establish focus
        el.inert = false
        el.dataset.interceptLoading = false
        focusedElement?.focus()

        // Replace content, either through morphing or dumb replacing
        const updateContent = () => {
          if (morph) {
            const newRootElement = Alpine.morph(el, newElement, {
              updating(from, to, childrenOnly, skip) {
                if (['INPUT', 'TEXTAREA', 'SELECT'].includes(to.nodeName)) {
                  from.value = to.defaultValue
                  to.value = to.defaultValue
                }

                if (from.isEqualNode(to)) {
                  skip()
                }
              },
            })

            // Notify interested parties about the interception
            newRootElement.dispatchEvent(
              new CustomEvent('interceptor:after', {
                bubbles: true,
                detail: {
                  morph,
                  viewTransition,
                  page,
                  queryParams,
                },
              }),
            )
          } else {
            el.replaceWith(newElement)

            // Notify interested parties about the interception
            newElement.dispatchEvent(
              new CustomEvent('interceptor:after', {
                bubbles: true,
                detail: {
                  morph,
                  viewTransition,
                  page,
                  queryParams,
                },
              }),
            )
          }
        }

        if (viewTransition && document.startViewTransition) {
          document.startViewTransition(updateContent)
        } else {
          updateContent()
        }
      })
      .catch(error => {
        console.error(error)
        window.location.replace(window.location.href)
      })
  }

  /**
   * Apply state to the browser history and replace content
   */
  function applyState({ url, page, queryParams, history = 'push' }) {
    // Simulate regular browser navigation
    window.history[history === 'replace' ? 'replaceState' : 'pushState'](
      {
        xIntercept: true,
        page,
        queryParams,
      },
      '',
      String(url)
        .replaceAll('%5B%5D', '[]')
        .replace(/\?q=$/, '')
        .replace(/&q=(&|$)/, '$1'),
    )

    // Replace content
    applyUpdatedContent({ page, queryParams })
  }

  /**
   * @param {CustomEvent} event
   */
  function manualUpdateHandler(event) {
    const {
      page = readPageFromPath(),
      queryParams = getQueryParamsObject(),
      navigate = true,
    } = event.detail ?? {}

    if (navigate) {
      applyState({
        url: generateUrl({ page, queryParams }),
        page,
        queryParams,
        history: typeof navigate === 'string' ? navigate : 'push',
      })
    } else {
      applyUpdatedContent({ page, queryParams })
    }
  }

  el.addEventListener('interceptor:update', manualUpdateHandler)

  /**
   * @param {MouseEvent} event
   */
  function paginationInterceptHandler(event) {
    // Bail if event was already handled to ensure no double requests are performed
    if (handledEvents.has(event)) return

    // Bail if any modifier key is pressed
    if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return

    // Bail if click target is not a link
    const link = event.target.closest('a')
    if (!link) return

    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(link.href)

    const currentPath = stripPageFromPath(currentUrl.pathname)

    const pathsAreEqual = targetUrl.pathname === currentPath
    const targetIsPagedCurrent = targetUrl.pathname.startsWith(currentPath + 'page/')

    // Bail if link does not point to current page
    if (!pathsAreEqual && !targetIsPagedCurrent) return

    const currentPage = readPageFromPath(currentUrl.pathname)
    const targetPage = readPageFromPath(targetUrl.pathname)

    // Prevent and bail if link does not change page
    if (currentPage === targetPage) {
      event.preventDefault()
      return
    }

    const queryParams = getQueryParamsObject(targetUrl)

    // Allow to cancel interception
    const interceptEvent = new CustomEvent('interceptor:before', {
      bubbles: true,
      cancelable: true,
      detail: {
        morph,
        viewTransition,
        page: targetPage,
        queryParams,
        targetUrl,
      },
    })
    el.dispatchEvent(interceptEvent)
    if (interceptEvent.defaultPrevented) return

    // Mark event as handled
    handledEvents.add(event)

    // Prevent browser-native navigation
    event.preventDefault()

    // Apply state and replace content
    applyState({
      url: targetUrl,
      page: targetPage,
      queryParams,
    })
  }

  // Handle pagination links
  el.addEventListener('click', paginationInterceptHandler)

  /**
   * @param {MouseEvent} event
   */
  function queryParamLinkInterceptHandler(event) {
    // Bail if event was already handled to ensure no double requests are performed
    if (handledEvents.has(event)) return

    // Bail if any modifier key is pressed
    if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return

    // Bail if click target is not a link
    const link = event.target.closest('a')
    if (!link) return

    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(link.href)

    // Get bare paths (without pagination)
    const currentPath = stripPageFromPath(currentUrl.pathname)
    const targetPath = stripPageFromPath(targetUrl.pathname)

    // Bail if link points to a different page
    if (currentPath !== targetPath) return

    // Bail if link does not change query parameters
    if (currentUrl.search === targetUrl.search) return

    const page = readPageFromPath(targetUrl.pathname)
    const queryParams = getQueryParamsObject(targetUrl)

    // Allow to cancel interception
    const interceptEvent = new CustomEvent('intercept:before', {
      bubbles: true,
      cancelable: true,
      detail: {
        endpoint,
        morph,
        page,
        queryParams,
        targetUrl,
      },
    })
    el.dispatchEvent(interceptEvent)
    if (interceptEvent.defaultPrevented) return

    // Mark event as handled
    handledEvents.add(event)

    // Prevent browser-native navigation
    event.preventDefault()

    // Apply state and replace content
    applyState({
      url: targetUrl,
      page,
      queryParams,
    })
  }

  // Handle query parameter links
  el.addEventListener('click', queryParamLinkInterceptHandler)

  /**
   * @param {SubmitEvent} event
   */
  function submitInterceptHandler(event) {
    // Bail if event was already handled to ensure no double requests are performed
    if (handledEvents.has(event)) return

    // Bail if click target is not a form
    if (event.target.nodeName !== 'FORM') return

    // Bail if target form does not use GET method
    if (event.target.method !== 'get') return

    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(event.target.action)

    // Reflect browser-native form submission behavior and reset URL parameters
    targetUrl.search = ''

    // Get bare paths (without pagination)
    const currentPath = stripPageFromPath(currentUrl.pathname)
    const targetPath = stripPageFromPath(targetUrl.pathname)

    // Bail if form does not submit to current page
    if (currentPath !== targetPath) return

    // Get applicable query parameters from form data
    const formData = new FormData(event.target)
    const formDataObject = parseFormData(formData)

    // Mirror form data in target URL parameters
    for (const [key, value] of formData) {
      targetUrl.searchParams.append(key, value)
    }

    const page = readPageFromPath(targetUrl.pathname)

    // Allow to cancel interception
    const interceptEvent = new CustomEvent('intercept:before', {
      bubbles: true,
      cancelable: true,
      detail: {
        endpoint,
        morph,
        page,
        queryParams: formDataObject,
        targetUrl,
      },
    })
    el.dispatchEvent(interceptEvent)
    if (interceptEvent.defaultPrevented) return

    // Mark event as handled
    handledEvents.add(event)

    // Prevent browser-native navigation
    event.preventDefault()

    // Apply state and replace content
    applyState({
      url: targetUrl,
      page,
      queryParams: formDataObject,
    })
  }

  el.addEventListener('submit', submitInterceptHandler, {
    capture: true,
  })

  // Handle browser-native backwards/forwards navigations
  window.addEventListener('popstate', popstateHandler)

  // Replace history item on initalization to make state available to popstate handler
  const currentUrl = new URL(window.location.href)
  window.history.replaceState(
    {
      xIntercept: true,
      page: readPageFromPath(currentUrl.pathname),
      queryParams: getQueryParamsObject(),
    },
    '',
    currentUrl,
  )

  cleanup(() => {
    el.removeEventListener('click', paginationInterceptHandler)
    el.removeEventListener('click', queryParamLinkInterceptHandler)
    el.removeEventListener('interceptor:update', manualUpdateHandler)
    el.removeEventListener('submit', submitInterceptHandler, { capture: true })
    window.removeEventListener('popstate', popstateHandler)
  })
}
